import {useEffect, useMemo, useContext, useRef} from "react";
import {Controller, useFieldArray, useForm, useWatch} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {Col, Row} from "antd";
import {
	Button, Loading
} from "~/components";
import {DateField, InputField, SelectField, InputPriceField} from "~/components/Forms";
import {AppContext} from "~/context/AppProvider";
import {userApi} from "~/api";
import {handleRequest} from "~/utils";
import {v4 as uuidv4} from 'uuid';

function PersonnelFormPay({item, onHandleSubmit}) {

	const {utilities} = useContext(AppContext);

	const listCommissionType = useMemo(() => {
		return [...Object.entries(utilities.payTypes).map(([key, label]) => {
			return {
				value: key,
				label: label
			};
		})];
	}, [utilities.payTypes]);

	const companyOptions = useMemo(() => {
		return [...Object.entries(utilities.company || {}).map(([key, value]) => {
			return {
				value: key,
				label: value.title
			};
		})];
	}, [utilities.company]);

	const initialValues = {
		pay: undefined,
		payTime: undefined,
		payType: 'nowage',
		payTypeTime: undefined,
		managerMilestone: [],
		salaryCompanies: [],
		salaryCompaniesTime: new Date()
	}

	const validationSchema = Yup.object().shape({
		managerMilestone: Yup.array().of(
			Yup.object().shape({
				from: Yup.number().required('Khoản từ không bỏ trống'),
				to  : Yup.number().required('Khoản đến không bỏ trống'),
				pay : Yup.number().required('Lương không bỏ trống')
			})
		),
		salaryCompanies: Yup.array().of(
			Yup.object().shape({
				companyId: Yup.string().required('Chọn công ty'),
				percent  : Yup.number().typeError('Nhập tỷ lệ %').moreThan(0, 'Tỷ lệ phải lớn hơn 0').required('Nhập tỷ lệ %')
			})
		).test('sum-100', 'Tổng tỷ lệ phân bổ phải bằng 100%', (arr) => {
			if(!arr || arr.length === 0) return true;
			const sum = arr.reduce((s, r) => s + (Number(r?.percent) || 0), 0);
			return sum === 100;
		}),
	})

	const {control, handleSubmit, formState: {isSubmitting, errors}, reset} = useForm({
		defaultValues: initialValues,
		resolver: yupResolver(validationSchema)
	});

	const {fields, append, remove} = useFieldArray({
		control,
		name: "managerMilestone"
	});

	const {fields: companyFields, append: appendCompany, remove: removeCompany, replace: replaceCompany} = useFieldArray({
		control,
		name: "salaryCompanies"
	});

	const watchCompanies = useWatch({control, name: "salaryCompanies"});

	const percentTotal = (watchCompanies || []).reduce((sum, row) => sum + (Number(row?.percent) || 0), 0);

	const watchSalaryTime = useWatch({control, name: "salaryCompaniesTime"});

	const lastFetchedKey = useRef(null);

	//Tải phân bổ lương theo công ty - chỉ khi đổi tháng hoặc đổi nhân viên (tránh xoá dòng vừa thêm)
	useEffect(() => {
		if(!item?.id || !watchSalaryTime) return;
		const d = new Date(watchSalaryTime);
		const monthKey = `${item.id}-${d.getFullYear()}-${d.getMonth() + 1}`;
		if(lastFetchedKey.current === monthKey) return;
		lastFetchedKey.current = monthKey;
		let active = true;
		const ts = Math.round(d.getTime() / 1000);
		(async () => {
			const [error, response] = await handleRequest(userApi.salaryCompanies({id: item.id, time: ts}));
			if(!active || error) return;
			const list = Array.isArray(response?.data) ? response.data : [];
			replaceCompany(list.map((row) => ({
				id: uuidv4(),
				companyId: String(row.companyId),
				percent: row.percent
			})));
		})();
		return () => { active = false; };
	}, [watchSalaryTime, item?.id]); // eslint-disable-line react-hooks/exhaustive-deps

	const manager = (item?.role.key == 'salesManager' || item?.role.key == 'salesSenior' || item?.role.key == 'salesSupper');

	useEffect(() => {
		if(item?.id) {
			initialValues.pay = item.pay
			initialValues.payType = item.payType
			const payManagerMilestone = JSON.parse(JSON.stringify(item.payManagerMilestone));
			payManagerMilestone.reduce((map, payMilestone) => {
				payMilestone.id = uuidv4();
				map.push(payMilestone);
				return map;
			}, []);
			initialValues.managerMilestone = payManagerMilestone;
		}
		reset(initialValues);
	}, [item]);

	const handleAddManagerMilestone = () => {
		append({ id: uuidv4(), from: 0, to: 0, pay: 0});
	}

	const handleAddSalaryCompany = () => {
		appendCompany({ id: uuidv4(), companyId: undefined, percent: 0});
	}

	const onSubmit = async (data) => {
		await onHandleSubmit(data, item);
	}

	return (
		<form className="form" onSubmit={handleSubmit(onSubmit)}>
			{isSubmitting && <Loading/>}
			<p className="heading">Lương cứng</p>
			<Row gutter={10}>
				<Col className="gutter-row" span={12}>
					<Controller control={control} name="pay" render={({ field }) => (
						<InputPriceField label="Lương" errors={errors} {...field}/>
					)}/>
				</Col>
				<Col className="gutter-row" span={12}>
					<Controller control={control} name="payTime" render={({ field }) => (
						<DateField label="Thời gian áp dụng" picker="month" errors={errors} {...field}/>
					)}/>
				</Col>
			</Row>
			<p className="heading">Lương kinh doanh</p>
			<Row gutter={10}>
				<Col className="gutter-row" span={12}>
					<Controller control={control} name="payType" render={({ field }) => (
						<SelectField label="Loại hoa hồng" options={listCommissionType} errors={errors} {...field} />
					)}/>
				</Col>
				<Col className="gutter-row" span={12}>
					<Controller control={control} name="payTypeTime" render={({ field }) => (
						<DateField label="Thời gian áp dụng" picker="month" errors={errors} {...field}/>
					)}/>
				</Col>
			</Row>
			{
				manager && (
					<>
						<p className="heading">Lương quản lý</p>
						<Row gutter={10}>
							<Col className="gutter-row" span={7}>
								<label htmlFor="">Từ</label>
							</Col>
							<Col className="gutter-row" span={7}>
								<label htmlFor="">Đến</label>
							</Col>
							<Col className="gutter-row" span={7}>
								<label htmlFor="">Lương</label>
							</Col>
							<Col className="gutter-row" span={3}></Col>
						</Row>
						{fields.map(({ id, from, to, pay }, index) => {
							return (
								<Row gutter={10} key={id}>
									<Col className="gutter-row" span={7}>
										<Controller control={control} name={`managerMilestone[${index}].from`} render={({ field }) => (
											<InputField defaultValue={from} errors={errors} {...field}/>
										)}/>
									</Col>
									<Col className="gutter-row" span={7}>
										<Controller control={control} name={`managerMilestone[${index}].to`} render={({ field }) => (
											<InputField defaultValue={to} errors={errors} {...field}/>
										)}/>
									</Col>
									<Col className="gutter-row" span={7}>
										<Controller control={control} name={`managerMilestone[${index}].pay`} render={({ field }) => (
											<InputField defaultValue={pay} errors={errors} {...field}/>
										)}/>
									</Col>
									<Col className="gutter-row" span={3}>
										<Button primary type="button" onClick={() => remove(index)}>Xóa</Button>
									</Col>
								</Row>
							);
						})}
						<div className="d-flex justify-content-end">
							<Button blue background type="button" onClick={handleAddManagerMilestone}>Thêm khoản lương</Button>
						</div>
					</>
				)
			}

			<p className="heading" style={{marginBottom: 0}}>Phân bổ lương theo công ty</p>
			<Row gutter={10} className="align-items-end">
				<Col className="gutter-row" span={24}>
					<Controller control={control} name="salaryCompaniesTime" render={({field}) => (
						<DateField label="Tháng áp dụng" picker="month" errors={errors} {...field}/>
					)}/>
				</Col>
			</Row>
			<p style={{fontSize: 12, color: '#64748b', marginTop: -4}}>
				Để trống = tính 100% cho công ty hiện tại của nhân viên. Đổi "Tháng áp dụng" để xem/sửa phân bổ từng tháng (chỉ ảnh hưởng báo cáo tài chính).
			</p>
			{companyFields.length > 0 &&
				<Row gutter={10}>
					<Col className="gutter-row" span={14}><label>Công ty</label></Col>
					<Col className="gutter-row" span={7}><label>Tỷ lệ (%)</label></Col>
					<Col className="gutter-row" span={3}></Col>
				</Row>
			}
			{companyFields.map(({id, companyId, percent}, index) => {
				return (
					<Row gutter={10} key={id}>
						<Col className="gutter-row" span={14}>
							<Controller control={control} name={`salaryCompanies[${index}].companyId`} render={({field}) => (
								<SelectField placeholder="Chọn công ty" options={companyOptions} errors={errors} {...field}/>
							)}/>
						</Col>
						<Col className="gutter-row" span={7}>
							<Controller control={control} name={`salaryCompanies[${index}].percent`} render={({field}) => (
								<InputField type="number" defaultValue={percent} errors={errors} {...field}/>
							)}/>
						</Col>
						<Col className="gutter-row" span={3}>
							<Button primary type="button" onClick={() => removeCompany(index)}>Xóa</Button>
						</Col>
					</Row>
				);
			})}
			<div className="d-flex justify-content-between align-items-center">
				<span style={{fontSize: 13, color: percentTotal === 100 || percentTotal === 0 ? '#059669' : '#ef4444'}}>
					Tổng tỷ lệ: {percentTotal}%
				</span>
				<Button blue background type="button" onClick={handleAddSalaryCompany}>Thêm công ty</Button>
			</div>
			{(errors?.salaryCompanies?.message || errors?.salaryCompanies?.root?.message) &&
				<p style={{color: '#ef4444', fontSize: 13, marginTop: 4}}>
					{errors.salaryCompanies.message || errors.salaryCompanies.root.message}
				</p>
			}

			<hr />
			<div className="d-flex justify-content-end">
				<Button primary type="submit">Cập nhật</Button>
			</div>
		</form>
	)
}

export default PersonnelFormPay;